/**
 * Hook for updating lastConnectedAt when the NWC client connects.
 *
 * Watches the shared NWC client status and updates the active connection's
 * lastConnectedAt field when it transitions to 'connected'. Uses a ref
 * to track the previous status to detect the transition.
 *
 * Tier: 2 (Lightning features, app-level infrastructure).
 *
 * @module lightning/hooks/useNwcConnectionTimestamp
 */

import { useEffect, useRef } from 'react';
import type { ConnectionStatus } from './useNwcClient';
import type { NwcConnectionRecord, NwcConnectionsDataSource } from './useNwcConnections';

/** Options for the useNwcConnectionTimestamp hook. */
export interface UseNwcConnectionTimestampOptions {
  /** Current NWC client connection status. */
  readonly status: ConnectionStatus;
  /** Currently active connection, or null. */
  readonly activeConnection: NwcConnectionRecord | null;
  /** Data source for persisting the timestamp update. */
  readonly dataSource: NwcConnectionsDataSource;
}

/**
 * Update lastConnectedAt when the NWC client transitions to 'connected'.
 *
 * Detects status transitions (not-connected → connected) using a ref.
 * Only fires once per transition to avoid redundant updates.
 *
 * @param options - Status, active connection, and data source
 */
export function useNwcConnectionTimestamp({
  status,
  activeConnection,
  dataSource,
}: UseNwcConnectionTimestampOptions): void {
  const prevStatusRef = useRef(status);

  useEffect(() => {
    if (prevStatusRef.current !== 'connected' && status === 'connected' && activeConnection) {
      dataSource.updateConnection(activeConnection.id, {
        lastConnectedAt: new Date().toISOString(),
      });
    }
    prevStatusRef.current = status;
  }, [status, activeConnection, dataSource]);
}
